import React, { useState } from 'react'
import { Link } from "react-router-dom";

// material-ui
import { Typography, Link as MuiLink, Stack, useMediaQuery, Chip, Tooltip, IconButton, Menu, MenuItem } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { CheckCircleOutlined, StopOutlined, DashOutlined, ProjectOutlined } from '@ant-design/icons'

// project import
import { useAuth } from 'services/hook/Auth'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import StringAvatar from 'components/common/StringAvatar'
import { getActionItems } from 'utils/util'
import moment from 'moment';

/**
 * Centralized cell renderer for tables
 */
const CellRenderer = ({ type, item, field, fieldName = 'name', isClickable = true, propsDetails = {}, link, navigate  }) => {
    switch (type) {
        case 'email': return <CellEmail item={item} />
        case 'phone': return <CellPhone item={item} />
        case 'status': return <CellStatus item={item} />
        case 'company': return <CellCompany item={item} />
        case 'datetime': return <CellDateTime item={item} field={field ?? type} />
        case 'user': return <CellUser item={item} events={propsDetails?.events} />
        case 'name': return <CellName item={item} events={propsDetails?.events} fieldName={fieldName} />
        case 'title': return <CellTitle item={item} isClickable={isClickable} events={propsDetails?.events} />
        case 'action': return <CellActions actions={propsDetails?.actions} item={item} />
        case 'moreaction': return <CellMoreActions actions={propsDetails?.actions} item={item} />
        case 'text': return <CellDefault item={item} field={field ?? type} />
        case 'link': return <CellLink item={item} isEvent={isEvent} events={propsDetails?.events} link={link} />
        case 'navigate': return <CellNavigate navigate={navigate} field={field} />
        case 'date': return <CellDate item={item} field={field ?? type}/>
        case 'relation': return <CellRelation item={item} field={field} relationField={fieldName} />
        default: return <CellDefault item={item} field={field ?? type} />
    }
}

/**
 * Render email column cell
 */
const CellEmail = ({ item }) => {
    return (
        <Typography variant="body">
            <MuiLink
                href={`mailto:${item.email}`}
                underline="none"
                onClick={(e) => e.stopPropagation()}
            >
                {item.email}
            </MuiLink>
        </Typography>
    )
}

/**
 * Render phone number column cell
 */
const CellPhone = ({ item }) => {
    return (
        <Typography variant="body">
            <MuiLink
                href={`tel:${item.phone}`}
                underline="none"
                onClick={(e) => e.stopPropagation()}
            >
                {item.phone}
            </MuiLink>
        </Typography>
    )
}

/**
 * Render name column cell
 */
const CellName = ({ item, events, fieldName }) => {
    const eventbus = useEventBusContext()
    const isMobile = useMediaQuery('(max-width:991px)')

    return (
        <Stack spacing={1} direction="row" alignItems="center">
            {!isMobile && <StringAvatar text={item.name} size="small" />}
            <MuiLink
                component="button"
                underline="none"
                sx={{ textAlign: 'start', fontSize: '16px' }}
                onClick={(e) => {
                    e.stopPropagation()
                    eventbus.fire(events.detail_init, item)
                }}
            >
                {item?.honorifics || ''} {item[fieldName]}
            </MuiLink>
        </Stack>
    )
}

/**
 * Render name column cell
 */
const CellTitle = ({ item, isClickable, events }) => {
    const eventbus = useEventBusContext()

    return (
        <Stack spacing={1} direction="row" alignItems="center">
            {isClickable ? 
                <MuiLink component="button" underline="none"
                    onClick={(e) => {
                        e.stopPropagation()
                        eventbus.fire(events.detail_init, item)
                    }}
                >
                    {item.title}
                </MuiLink>
            : 
                <Typography variant='body2'>{item.title}</Typography>
            }
        </Stack>
    )
}

/**
 * Render name column cell
 */
const CellLink = ({ item, isEvent, events, link }) => {
    const eventbus = useEventBusContext()

    return (
        <Stack spacing={1} direction="row" alignItems="center">
            {isEvent ? 
                <MuiLink component="button" underline="none"
                    onClick={(e) => {
                        e.stopPropagation()
                        eventbus.fire(events.detail_init, item)
                    }}
                >
                    {item.title}
                </MuiLink>
            : 
                <MuiLink component="button" underline="none"
                   to={link}
                >
                    {item.title}
                </MuiLink>
            }
        </Stack>
    )
}

const CellNavigate = ({navigate, field}) => {

    return (
        <Tooltip title="Project">
            <MuiLink component="button" underline="none" onClick={(e) => { navigate()} } >
                { field }
            </MuiLink>
        </Tooltip>
    )
}
    


/**
 * Render name column cell
 */
const CellUser = ({ item, events }) => {
    const eventbus = useEventBusContext()
    const isMobile = useMediaQuery('(max-width:991px)')

    return (
        <Stack spacing={1} direction="row" alignItems="center">
            {!isMobile && <StringAvatar text={item.name} size="small" />}
            <MuiLink
                component="button"
                underline="none"
                onClick={(e) => {
                    e.stopPropagation()
                }}
                sx={{ textAlign: 'left' }}
            >
                {item.name}
            </MuiLink>
        </Stack>
    )
}

/**
 * Render status column cell
 */
const CellStatus = ({ item }) => {
    const color = item.status == 'Blocked' ? 'redLight' : 'greenLight'
    const statusicon = item.status == 'Blocked' ? <StopOutlined /> : <CheckCircleOutlined />
    return (<Chip label={item.status} size="small" icon={statusicon} color={color} sx={{ mt: '5px' }} />)
}

/**
 * Render cell to show company details.
 */
const CellCompany = ({ item }) => {
    return(
        <Stack direction='column'>
            {(item.company_name != null && item.company_name != '') && <Typography variant="body">{item.company_name}</Typography>}
            {(item.company_address != null && item.company_address != '') && <Typography variant="body">{item.company_address.substr(0, 40)}...</Typography>}
        </Stack>
    )
}

/**
 * Fallback renderer for any unspecified cell type
 */
const CellDefault = ({ item, field }) => {
    const value = item?.[field];
    return (
        <Typography variant="h5" sx={{ fontWeight: 400 }}> {value !== null && value !== undefined ? String(value).slice(0, 40) + (String(value).length > 40 ? '...' : '') : ''} </Typography>
    )
}

/**
 * Fallback renderer for any unspecified cell type
 */
const CellDateTime = ({ item, field }) => {
    return (
        <Typography variant="body">
            {moment(item[field]).format('d-m-YYYY,  h:mm A')}
        </Typography>
    )
}

/**
 * Fallback renderer for any unspecified cell type date
 */
const CellDate = ({ item, field }) => {
    return (
        <Typography variant="h5" sx={{fontWeight: '400'}}>
            {item[field] ? moment(item[field]).format('DD-MM-YYYY') : ''}
        </Typography>
    )
}

const CellRelation = ({ item, field, relationField = 'name' }) => {

    if (!item || !item[field]) {
        return <Typography variant="body">-</Typography>
    }

    const value = item[field] ?? '';
    return (
        <Typography variant="body">
            {value}
        </Typography>
    );
};

/**
 * Generic reusable item action button
 * FIXED: Properly handles links without requiring onClick prop
 */
const ActionButton = ({ item, tooltip, icon, event, payload, onClick, link = null }) => {
    const eventbus = useEventBusContext();
    
    const handleClick = (e) => {
        e.stopPropagation()
        if (onClick && typeof onClick === 'function') {
            onClick(e, payload)
        } else if (event) {
            eventbus.fire(event, payload)
        }
    }

    // If link exists, render as Link component
    if (link && link !== null) {
        return (
            <Tooltip title={tooltip}>
                <IconButton 
                    color="primary" 
                    component={Link} 
                    to={typeof link === 'function' ? link(payload) : link}
                    onClick={(e) => e.stopPropagation()}
                > 
                    {icon} 
                </IconButton>
            </Tooltip>
        )
    }

    // Otherwise, render as regular button with event handling
    return (
        <Tooltip title={tooltip}>
            <IconButton color="primary" onClick={handleClick}>
                {icon}
            </IconButton>
        </Tooltip>
    )
}

/**
 * Render action column cell with permission-based controls
 */
const CellActions = ({ item, actions }) => {
    const { checkPermission } = useAuth()
    const actionitems = getActionItems(item, actions);

    return (<Stack direction='row' justifyContent='center'>
        {actionitems.map((action, idx) =>
            checkPermission(action.permission) && (
                <ActionButton 
                    key={idx} 
                    tooltip={action.tooltip} 
                    icon={action.icon} 
                    event={action.event} 
                    payload={action.payload}
                    onClick={action.onClick}
                    link={action.link}
                />
            )
        )}
    </Stack>)
}

const CellDropdonwMenu = ({ open, anchorEl, dropdownActions, checkPermission, handleMenuOpen, handleMenuClose }) => {
    const theme = useTheme();
    const eventbus = useEventBusContext();
    const menuitemsx = { display: 'flex', alignItems: 'center', minHeight: 'auto', p: '8px 16px', '& .anticon': { color: theme.palette.primary.main, mr: '10px' } }
    return(
        <>
            <IconButton color="primary" onClick={handleMenuOpen}>
                <DashOutlined />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                {dropdownActions.map((action, idx) =>
                    checkPermission(action.permission) && (
                        <MenuItem sx={menuitemsx}
                            key={idx}
                            onClick={(e) => {
                                handleMenuClose();
                                if (action.onClick) {
                                    action.onClick(e, action.payload);
                                } else {
                                    eventbus.fire(action.event, action.payload);
                                }
                            }}
                        >
                            {action.icon} {action.menu}
                        </MenuItem>
                    )
                )}
            </Menu>
        </>
    )
}

/**
 * Render action column cell with permission-based controls
 */
const CellMoreActions = ({ item, actions }) => {
    const actionitems = getActionItems(item, actions);
    const { checkPermission } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const visibleActions = actionitems.slice(0, 2);
    const dropdownActions = actionitems.slice(2);

    return (
        <Stack direction="row" justifyContent="center">
            {visibleActions.map((action, idx) => {
                const allowed = checkPermission(action.permission);

                if (allowed) {
                    return (
                        <ActionButton
                            key={idx}
                            tooltip={action.tooltip}
                            icon={action.icon}
                            event={action.event}
                            payload={action.payload}
                            onClick={action.onClick}
                            link={action.link ?? null}
                        />
                    );
                }

                return null;
            })}

            {dropdownActions.some(a => checkPermission(a.permission)) && 
                <CellDropdonwMenu 
                    open={open} 
                    anchorEl={anchorEl} 
                    dropdownActions={dropdownActions} 
                    checkPermission={checkPermission}
                    handleMenuOpen={handleMenuOpen} 
                    handleMenuClose={handleMenuClose} 
                />
            }
        </Stack>
    );
};

export default CellRenderer